import api from "../../../utils/api";
import { EXPENSES_ENDPOINTS, type PayrollHistory } from "@torre/expenses";
import { fetchAccess } from "./expensesService";

// ---------------------------------------------------------------------------
// Estados de cuenta cifrados + nómina pagada (#1027). Solo dueños: el servidor
// responde 403 a cualquiera más, así que el front no ofrece estas pantallas si
// `GET /expensesAccess` no dice `owner: true`.
// ---------------------------------------------------------------------------

const E = EXPENSES_ENDPOINTS;
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const data = <T>(res: any): T => res?.data?.data as T;

export interface StatementDoc {
  id: string;
  label: string;
  issuer: string | null;
  importId: string | null;
  file: { name: string; contentType: string; size: number };
  encrypted: boolean;
  createdAt: string;
}

export type PayrollHistoryResponse = PayrollHistory & { payoutConfigured: boolean };

export const fetchPayrollHistory = async (params: { from?: string; to?: string }) =>
  data<PayrollHistoryResponse>(await api.get(E.payrollHistory, { params }));

export const fetchStatements = async () => data<StatementDoc[]>(await api.get(E.statementList));

export async function uploadStatement(file: File, label: string) {
  const form = new FormData();
  form.append("file", file);
  form.append("label", label);
  return data<StatementDoc>(await api.post(E.statementUpload, form, { ...MULTIPART, timeout: 120_000 }));
}

export const requestStatementCode = async () => data<{ sentTo: string | null; expiresAt: string }>(await api.post(E.statementCode, {}));
export const unlockStatements = async (code: string) => data<{ unlockedUntil: string }>(await api.post(E.statementUnlock, { code }));
export const fetchStatementAccess = async () => data<{ unlockedUntil: string | null }>(await api.get(E.statementAccess));

/** Error de la descarga: con `responseType: blob` el cuerpo JSON llega como Blob. */
export class StatementFileError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/** Archivo descifrado. Lanza `StatementFileError` con 423 si hace falta el código del correo. */
export async function fetchStatementFile(id: string): Promise<Blob> {
  try {
    const res = await api.get(E.statementFile, { params: { id }, responseType: "blob" });
    return res.data as Blob;
  } catch (err) {
    const r = (err as { response?: { status?: number; data?: unknown } })?.response;
    let message = "";
    if (r?.data instanceof Blob) {
      try {
        message = JSON.parse(await r.data.text())?.meta?.message || "";
      } catch {
        /* cuerpo no JSON */
      }
    }
    throw new StatementFileError(message, r?.status || 0);
  }
}

// --- ¿Es dueño? Una pregunta por usuario y pestaña ------------------------------
let ownerCache: { userId: string; at: number; value: boolean } | null = null;
let ownerInflight: { userId: string; promise: Promise<boolean> } | null = null;
const OWNER_TTL_MS = 5 * 60 * 1000;

export function resolveExpensesOwner(): Promise<boolean> {
  const userId = typeof localStorage !== "undefined" ? localStorage.getItem("userId") : null;
  if (!userId || !localStorage.getItem("token")) return Promise.resolve(false);
  if (ownerCache && ownerCache.userId === userId && Date.now() - ownerCache.at < OWNER_TTL_MS) return Promise.resolve(ownerCache.value);
  if (ownerInflight && ownerInflight.userId === userId) return ownerInflight.promise;
  const promise = fetchAccess()
    .then((a) => {
      const value = !!(a.allowed && a.owner);
      // Solo se guarda una respuesta real: un error de red no esconde el menú 5 minutos.
      ownerCache = { userId, at: Date.now(), value };
      return value;
    })
    .catch(() => false)
    .finally(() => {
      if (ownerInflight && ownerInflight.promise === promise) ownerInflight = null;
    });
  ownerInflight = { userId, promise };
  return promise;
}

/** Solo para pruebas. */
export function _resetOwnerCache() {
  ownerCache = null;
  ownerInflight = null;
}
